/**
 * @file MilkPowderBulkCostModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-19
 * @modified 2026-09-07
 * @description Bulk version of MilkPowderCostModal — set one week-commencing and
 *              edit the cost (and notes) for every active milk powder product in
 *              one go. Each row is prefilled with the product's current cost;
 *              clearing a row's cost skips it. Saves via the bulk endpoint.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import Input from './Input';
import DateInput from './DateInput';
import { formatCurrency } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';
import Textarea from './Textarea';
import { parsePaste, matchProducts } from '../utils/milkPowderPaste';

/*
 * No prefilled date, deliberately.
 *
 * It used to default to the Monday of the current week, which is wrong twice
 * over: a supplier's list is effective from the day it is issued — the stored
 * dates are Thursdays and Fridays as often as Mondays — and a date the user
 * never chose is a date nobody checks. A batch entered on 4 September was
 * filed under 31 August because of it.
 *
 * Both modals already refuse to save without one, so an empty field is a
 * question rather than a gap.
 */
function MilkPowderBulkCostModal({ products, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [week, setWeek] = useState('');   // no prefill — see the note above

  useSlideOutButton([loading]);

  const [rows, setRows] = useState(() => (products || []).map(p => ({
    inventory_item_id: p.inventory_item_id,
    item_code: p.item_code,
    item_name: p.item_name,
    current_cost: p.current_cost,
    cost: p.current_cost != null ? String(p.current_cost) : '',
    notes: ''
  })));

  /*
   * Paste the supplier's list rather than retype twelve figures.
   *
   * It arrives as a name on one line and a price on the next, in the
   * supplier's shorthand — "OPTI LK", "OPTI GOLD + CLOSTRI", "OPTI 100 > OPTI
   * HS 40". utils/milkPowderPaste does the matching; this holds the result so
   * it can be REVIEWED before anything is written into the cost fields. These
   * are costs: a confident wrong match is worse than an obvious gap, so
   * nothing is applied until Apply is pressed and every row shows what it
   * matched to.
   */
  const [paste, setPaste] = useState('');
  const [preview, setPreview] = useState(null);

  const readPaste = () => {
    const parsed = parsePaste(paste);
    if (!parsed.length) {
      setError('Nothing in that paste looked like a name followed by a price.');
      return;
    }
    setError('');
    setPreview(matchProducts(parsed, rows));
  };

  const applyPreview = () => {
    const byId = new Map();
    for (const m of preview || []) {
      if (m.product && m.price != null) byId.set(m.product.inventory_item_id, m.price);
    }
    setRows((prev) => prev.map((r) => (byId.has(r.inventory_item_id)
      ? { ...r, cost: String(byId.get(r.inventory_item_id)) }
      : r)));
    setPreview(null);
    setPaste('');
  };

  useEffect(() => {
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) contentWrapper.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
    };
  }, []);

  const setRow = (id, field, value) => {
    setRows(prev => prev.map(r => (r.inventory_item_id === id ? { ...r, [field]: value } : r)));
    setError('');
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => onClose(), 200);
  };

  const filledCount = rows.filter(r => r.cost !== '' && !isNaN(parseFloat(r.cost))).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!week) { setError('Please select a week-commencing date'); return; }
    const items = rows
      .filter(r => r.cost !== '' && !isNaN(parseFloat(r.cost)))
      .map(r => ({ inventory_item_id: r.inventory_item_id, cost: parseFloat(r.cost), notes: r.notes || null }));
    if (items.length === 0) { setError('Enter a cost for at least one product'); return; }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pricing/milk-powder/cost-prices/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ week_commencing: week, items })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save cost prices');
      }
      onSave();
    } catch (err) {
      console.error('Error bulk-saving milk powder cost prices:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content weekly-cost-modal weekly-cost-bulk-modal modal-lg ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h4 className="modal-title">Set Weekly Costs — All Active Products</h4>
          <button className="btn-icon close-btn" onClick={handleClose} title="Close">
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: 'var(--spacing-md) var(--spacing-xl) var(--spacing-xl)' }}>
            {error && (
              <div className="error-message">
                <span className="material-symbols-rounded">error</span>
                {error}
              </div>
            )}

            <div className="mp-bulk-top">
              <div className="form-group">
                <DateInput
                  label="Week Commencing *"
                  value={week}
                  onChange={(e) => setWeek(e.target.value)}
                  required
                />
              </div>
              <div className="mp-bulk-hint">
                <span className="material-symbols-rounded">info</span>
                <span>
                  Each row is prefilled with its current cost. Edit any you want to change and clear a
                  field to skip it. <strong>{filledCount}</strong> of {rows.length} will be saved for this week.
                </span>
              </div>
            </div>

            <details className="mp-bulk-paste">
              <summary>Paste a supplier price list</summary>
              {!preview && (
                <>
                  <Textarea
                    rows={6}
                    value={paste}
                    onChange={(e) => setPaste(e.target.value)}
                    placeholder={'BLEND 20-20\n£1,995\nOPTI 15\n£1,995\n…'}
                  />
                  <button type="button" className="btn-secondary" onClick={readPaste} disabled={!paste.trim()}>
                    Read list
                  </button>
                </>
              )}

              {preview && (
                <>
                  <table className="mp-paste-table">
                    <thead>
                      <tr><th>On the list</th><th>Is our product</th><th className="num">Price</th></tr>
                    </thead>
                    <tbody>
                      {preview.map((m, i) => (
                        <tr key={i} className={m.product ? '' : 'unmatched'}>
                          <td>{m.name}</td>
                          <td>
                            {/* Every match is correctable. The matcher is good,
                                not certain — "OPTI LK" is the same words as two
                                different pack sizes. */}
                            <select
                              value={m.product?.inventory_item_id ?? ''}
                              onChange={(e) => {
                                const id = e.target.value ? Number(e.target.value) : null;
                                setPreview(preview.map((x, j) => (j === i
                                  ? { ...x, product: rows.find((r) => r.inventory_item_id === id) || null }
                                  : x)));
                              }}
                            >
                              <option value="">— don't apply —</option>
                              {rows.map((r) => (
                                <option key={r.inventory_item_id} value={r.inventory_item_id}>
                                  {r.item_name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="num">
                            {formatCurrency(m.price)}
                            {m.product?.current_cost != null
                              && Number(m.product.current_cost) !== m.price && (
                              <em className="mp-paste-was"> was {formatCurrency(m.product.current_cost)}</em>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mp-paste-actions">
                    <button type="button" className="btn-secondary" onClick={() => setPreview(null)}>Back</button>
                    <button type="button" className="btn-primary" onClick={applyPreview}>
                      Fill {preview.filter((m) => m.product).length} cost
                      {preview.filter((m) => m.product).length === 1 ? '' : 's'}
                    </button>
                  </div>
                  <p className="mp-paste-note">
                    Filling only puts the figures in the fields below — nothing is saved
                    until you press Save.
                  </p>
                </>
              )}
            </details>

            <div className="mp-bulk-list">
              <div className="mp-bulk-row mp-bulk-head">
                <div>Product</div>
                <div className="mp-bulk-current-col">Current</div>
                <div>New cost (£)</div>
                <div>Notes</div>
              </div>
              {rows.map(r => (
                <div key={r.inventory_item_id} className="mp-bulk-row">
                  <div className="mp-bulk-prod">
                    {r.item_code && <span className="product-code">{r.item_code}</span>}
                    <span className="mp-bulk-name" title={r.item_name}>{r.item_name}</span>
                  </div>
                  <div className="mp-bulk-current-col">
                    {r.current_cost != null ? formatCurrency(r.current_cost) : '—'}
                  </div>
                  <Input
                    type="number"
                    value={r.cost}
                    onChange={(e) => setRow(r.inventory_item_id, 'cost', e.target.value)}
                    placeholder="0.00"
                  />
                  <Input
                    type="text"
                    value={r.notes}
                    onChange={(e) => setRow(r.inventory_item_id, 'notes', e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="modal-footer" style={{ position: 'absolute', bottom: '0', left: '0', right: '0' }}>
            <button type="submit" className="btn-slideout btn-slideout-primary" disabled={loading || filledCount === 0}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>
                  {loading ? 'sync' : 'save'}
                </span>
              </div>
              <span className="btn-label">{loading ? 'Saving...' : `Save ${filledCount} Cost${filledCount === 1 ? '' : 's'}`}</span>
            </button>
            <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose} disabled={loading}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">close</span>
              </div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default MilkPowderBulkCostModal;
